// Sidebar summary + (collapsed) pipeline details for the demo screening.
//
// Layout (top → bottom):
//   1. status eyebrow      (pulse + Screening… / Complete in N.Ns / Error)
//   2. headline             one big sentence the buyer can repeat to
//                           their boss: "3,142 parcels → 4 viable sites"
//                           with "Screened in N.Ns" beneath. Only visible
//                           once the `complete` SSE arrives.
//   3. tallies              human-language version of the funnel — the
//                           list a project director can read top-to-
//                           bottom and understand. Keeps the Norwegian
//                           data-source names (Matrikkelen, kommune
//                           planregister, Artsdatabanken) because they
//                           are credibility-load-bearing.
//   4. pipeline details     the raw stream log — collapsed by default
//                           behind "Show pipeline details ▾". Engineers
//                           can expand to see every SSE frame.
//
// All counts are derived from real engine events. The Frontend never
// invents a number. If a step never fired (engine bailed out early),
// the corresponding row is suppressed rather than shown as 0.

import React, { useState } from 'react';

function fmtCount(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-US');
}

// Pull the latest count for a given stage from the events array. The
// engine emits multiple events per stage (a "starting" message, then a
// "count" event); we always want the last one carrying a count.
function latestCount(events, stage) {
  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    if (ev?.stage === stage && typeof ev.count === 'number') return ev.count;
  }
  return null;
}

// Reads enrichment progress as a single completed count (when finished)
// or null while in flight — the live "X/Y" progress is displayed in the
// detail log instead, never as a tally.
function enrichmentDone(events) {
  for (let i = events.length - 1; i >= 0; i--) {
    const ev = events[i];
    if (ev?.stage === 'enriching'
        && typeof ev.progress === 'number'
        && typeof ev.total === 'number'
        && ev.progress >= ev.total
        && ev.total > 0) {
      return ev.total;
    }
  }
  return null;
}

// Maps a raw SSE stage to a plain-English label for the (collapsed)
// pipeline detail view. Keeps engineers honest about what the engine is
// actually doing while staying readable for non-engineers who choose
// to expand the section.
const STAGE_LABEL = {
  substations: 'Substations within 5 km',
  teiger:      'Land parcels (Matrikkelen)',
  pre_filter:  'Pre-filter for size and access',
  assembling:  'Assembled adjacent parcels',
  zone_check:  'Land use classification (kommune planregister)',
  enriching:   'Environmental enrichment (Artsdatabanken)',
  complete:    'Final ranking',
};

function describeEvent(ev) {
  const bits = [];
  if (typeof ev.count === 'number')
    bits.push(`${fmtCount(ev.count)}`);
  if (typeof ev.total === 'number' && ev.total !== ev.count)
    bits.push(`of ${fmtCount(ev.total)}`);
  if (typeof ev.progress === 'number' && typeof ev.total === 'number')
    bits.push(`${ev.progress}/${ev.total}`);
  if (ev.message) bits.push(ev.message);
  if (ev.elapsed_s != null) bits.push(`${ev.elapsed_s}s`);
  return bits.join(' · ');
}

// While streaming, every enrichment progress event spams a separate
// row. Once the run is done they collapse into one final row so the
// expanded log isn't dominated by "0/11, 1/11, 2/11…". We keep them
// all live while loading because watching the count tick is part of
// the realism.
function maybeCollapseEvents(events, isComplete) {
  if (!isComplete) return events;
  const out = [];
  let lastEnriching = null;
  let injectedCollapsed = false;
  for (const ev of events) {
    if (ev?.stage === 'enriching' && typeof ev.progress === 'number') {
      lastEnriching = ev;
      continue;
    }
    // Inject the single collapsed enrichment row right before `complete`
    // so pipeline order reads correctly top-to-bottom.
    if (!injectedCollapsed && ev?.stage === 'complete' && lastEnriching) {
      out.push({
        ...lastEnriching,
        count: lastEnriching.total,
        _collapsed: true,
      });
      injectedCollapsed = true;
    }
    out.push(ev);
  }
  if (!injectedCollapsed && lastEnriching) {
    out.push({
      ...lastEnriching,
      count: lastEnriching.total,
      _collapsed: true,
    });
  }
  return out;
}

// One row of the customer-language tally list. Suppressed entirely if
// the count is null/undefined so steps that never fired don't show as
// "—".
function Tally({ label, value }) {
  if (value == null) return null;
  return (
    <div className="dsp-tally-row">
      <span className="dsp-tally-check" aria-hidden="true">✓</span>
      <span className="dsp-tally-label">{label}</span>
      <span className="dsp-tally-count">{fmtCount(value)}</span>
    </div>
  );
}

export default function DemoSearchProgress({
  events = [],
  loading = false,
  error = null,
  results = [],
  hoveredIdx = -1,
  onHoverIdx,
  onSiteClick,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const completeEvent = events.find((e) => e?.stage === 'complete') || null;
  const isComplete = !!completeEvent;
  const meta = completeEvent?.search_meta || null;
  const secs = meta?.search_duration_seconds;

  let status = 'idle';
  if (loading) status = 'streaming';
  else if (error) status = 'error';
  else if (isComplete) status = 'done';

  let headline = 'Idle';
  // /demo replays a pre-captured fixture with Helgeland parcels
  // (frontend/src/demo/demoFixture.json — lat ~66.7, Mo i Rana
  // area). Keep this headline aligned with the fixture data. The
  // marketing copy + /app?test_region=stavanger free tier mention
  // Stavanger — those use the LIVE engine, not this fixture.
  if (status === 'streaming') headline = 'Screening Helgeland (Nordland)…';
  if (status === 'done') headline = secs != null ? `Complete in ${secs}s` : 'Complete';
  if (status === 'error') headline = 'Error';

  // ── Counts for the customer-language tallies ─────────────────────
  // Prefer the authoritative `search_meta` over event scraping when
  // available; fall back to the latest stage event so a slow engine
  // still shows something during the stream.
  const substationsN = meta?.substations_found ?? latestCount(events, 'substations');
  const parcelsN     = meta?.teiger_loaded     ?? latestCount(events, 'teiger');
  const preFilterN   = meta?.teiger_pre_filtered ?? latestCount(events, 'pre_filter');
  const assemblagesN = latestCount(events, 'assembling');
  const zoneN        = meta?.assemblages_found ?? latestCount(events, 'zone_check');
  const enrichedN    = meta?.teiger_enriched   ?? enrichmentDone(events);
  const resultsN     = meta?.results_returned  ?? null;

  const detailEvents = maybeCollapseEvents(events, isComplete);

  return (
    <div className="dsp-root">
      <div className="dsp-eyebrow">
        <span
          className={'dsp-pulse' + (status === 'streaming' ? ' is-active' : '')}
        />
        <span>{headline}</span>
      </div>

      {error && status === 'error' && (
        <div className="dsp-error">{error}</div>
      )}

      {/* Headline summary — the single line the buyer takes to their
          boss. Only rendered on a successful `complete` event. */}
      {isComplete && parcelsN != null && resultsN != null && (
        <div className="dsp-headline">
          <div className="dsp-headline-main">
            <span className="dsp-headline-parcels">{fmtCount(parcelsN)}</span>
            <span className="dsp-headline-label">parcels</span>
            <span className="dsp-headline-arrow">→</span>
            <span className="dsp-headline-viable">{fmtCount(resultsN)}</span>
            <span className="dsp-headline-label">viable sites</span>
          </div>
          {secs != null && (
            <div className="dsp-headline-sub">
              Screened in {Number(secs).toFixed(1)} seconds
            </div>
          )}
        </div>
      )}

      {/* Customer-language tallies — visible once we have anything
          worth showing (during streaming the counts populate live as
          stages fire). */}
      {(events.length > 0 || isComplete) && (
        <div className="dsp-tallies">
          <Tally label="Substations within 5 km · matched"               value={substationsN} />
          <Tally label="Land parcels (Matrikkelen) · loaded"             value={parcelsN} />
          <Tally label="Pre-filter for size and access · remaining"     value={preFilterN} />
          <Tally label="Assembled adjacent parcels · candidate sites"   value={assemblagesN} />
          <Tally label="Land use (kommune planregister) · passed"       value={zoneN} />
          <Tally label="Environmental enrichment (Artsdatabanken)"      value={enrichedN} />
          <Tally label="Final ranking · viable sites"                   value={resultsN} />
        </div>
      )}

      {/* Ranked-site list — cross-hover with the map markers via
          hoveredIdx state in DemoApp. Each row mirrors a numbered
          marker on the map so the buyer can read the dots as "site 1
          is here, site 2 is here" rather than four anonymous teal
          dots. Suppressed until results arrive. */}
      {results.length > 0 && (
        <div className="dsp-ranked">
          <div className="dsp-ranked-title">Top {results.length} viable sites</div>
          {results.map((r, i) => {
            const kv = r.nearest_grid_voltage_kv ? `${r.nearest_grid_voltage_kv} kV` : null;
            const km = (r.nearest_grid_distance_km != null)
              ? `${Number(r.nearest_grid_distance_km).toFixed(1)} km`
              : null;
            const meta = [kv, km].filter(Boolean).join(' · ');
            const isHov = hoveredIdx === i;
            return (
              <div
                key={`ranked-${i}`}
                className={
                  'dsp-ranked-row'
                  + (i === 0 ? ' is-top' : '')
                  + (isHov ? ' is-hovered' : '')
                }
                onMouseEnter={() => onHoverIdx?.(i)}
                onMouseLeave={() => onHoverIdx?.(-1)}
                onClick={() => onSiteClick?.(r)}
                role="button"
                tabIndex={0}
              >
                <span className="dsp-ranked-rank">{i + 1}</span>
                <span className="dsp-ranked-name">
                  {r.site_label || `Site ${i + 1}`}
                </span>
                <span className="dsp-ranked-meta">{meta || '—'}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Pipeline details — collapsed by default. The engineer audience
          can expand to see every SSE frame; the marketing audience will
          ignore it. */}
      {events.length > 0 && (
        <details
          className="dsp-details"
          open={detailsOpen}
          onToggle={(e) => setDetailsOpen(e.currentTarget.open)}
        >
          <summary className="dsp-details-summary">
            {detailsOpen ? 'Hide pipeline details' : 'Show pipeline details'}
          </summary>
          <ol className="dsp-stream">
            {detailEvents.map((ev, i) => {
              const label = STAGE_LABEL[ev.stage] || ev.stage || 'event';
              const detail = describeEvent(ev);
              const isFinal = ev.stage === 'complete';
              return (
                <li
                  key={`${ev.stage || 'e'}-${i}`}
                  className={'dsp-row' + (isFinal ? ' is-final' : '')}
                >
                  <div className="dsp-row-title">{label}</div>
                  {detail && <div className="dsp-row-detail">{detail}</div>}
                </li>
              );
            })}
          </ol>
        </details>
      )}
    </div>
  );
}
